import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { Aircraft, AircraftProps } from '@/domain/models/Aircraft'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'
import { FlightState } from '@/domain/states/FlightState'

import {
  AircraftDTO,
  GetAircraftsOptions,
  IUserAircraftsRepository,
  RequestGetAircraftsResponse
} from './IUserAircraftsRepository'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { DexieDB } from '@/infra/localDB/DexieDB'
import { DexieWBProfile } from '@/infra/localDB/IDexieDB'
import { normalizeUnit, unitDomains } from '@/utils/unit'

@injectable()
export class UserAircraftsRepository implements IUserAircraftsRepository {
  private userAircrafts: Aircraft[]

  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState,
    @inject(InjectionTokens.FlightState) private flightState: FlightState,
    @inject(InjectionTokens.DexieDB) private localDB: DexieDB
  ) {}

  protected getRemoteUrl(): string {
    return `${NEXATLAS_API_BASE}/userAircraft`
  }

  async init(): Promise<Result<void>> {
    try {
      const aircraftsResult = await this._getRemoteAircrafts()
      if (aircraftsResult.isFailure) {
        return Result.fail(aircraftsResult.error)
      }
      this.userAircrafts = aircraftsResult.getValue()
      const activeAircraftId = this.flightState?.getStateSnapshot()?.activeAircraft?.id
      if (activeAircraftId) {
        const aircraft = await this.getAircraft(activeAircraftId)
        if (aircraft.isFailure) {
          return Result.fail(aircraft.error)
        }
        this.flightState.setActiveAircraft(aircraft.getValue())
      }

      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to initialize UserAircraftsRepository: ${error}`)
    }
  }

  // AIRCRAFT METHODS

  async getAircrafts(forceRefresh = false, options: GetAircraftsOptions = {}): Promise<Result<Aircraft[]>> {
    const selectAircrafts = (aircrafts: Aircraft[]) =>
      options.includeDeleted ? [...aircrafts] : aircrafts.filter((aircraft) => !aircraft.deletedAt)

    try {
      if (this.userAircrafts && !forceRefresh) {
        return Result.ok(selectAircrafts(this.userAircrafts))
      }

      const aircraftsResult = await this._getRemoteAircrafts()
      if (aircraftsResult.isFailure) {
        return Result.fail(aircraftsResult.error)
      }

      this.userAircrafts = aircraftsResult.getValue()
      return Result.ok(selectAircrafts(this.userAircrafts))
    } catch (error) {
      console.error('Error in getAircrafts:', error)
      return Result.fail(`Error fetching aircrafts: ${error}`)
    }
  }

  /**
   * One aircraft by id, **removed ones included**. Whoever asks by id already holds a reference to
   * it — the aircraft of a saved W&B fill, for instance — and removal is soft: the fill stays the
   * pilot's and keeps resolving its aircraft (ADR-014 of the pilotapp). Before, a removed aircraft
   * resolved only when the cache happened to be warm, because a cold load went through the filtered
   * list.
   */
  async getAircraft(id: UniqueEntityID): Promise<Result<Aircraft>> {
    try {
      const aircraftsResult = await this.getAircrafts(false, { includeDeleted: true })
      if (aircraftsResult.isFailure) {
        return Result.fail(aircraftsResult.error)
      }

      const aircrafts = aircraftsResult.getValue()
      if (!aircrafts || aircrafts.length === 0) {
        console.error('No aircrafts available to search for id:', id.toString())
        return Result.fail('No aircrafts available')
      }

      const aircraft = aircrafts.find((candidate) => candidate.id.equals(id))
      if (!aircraft) {
        console.error('Aircraft not found with id:', id.toString())
        return Result.fail('Aircraft not found')
      }

      return Result.ok(aircraft)
    } catch (error) {
      console.error('Error in getAircraft:', error)
      return Result.fail(`Error fetching aircraft: ${error}`)
    }
  }

  async getAircraftsBatch(ids: UniqueEntityID[]): Promise<Result<Aircraft[]>> {
    try {
      const allAircraftsResult = await this.getAircrafts()
      if (allAircraftsResult.isFailure) {
        return Result.fail(allAircraftsResult.error)
      }

      if (!allAircraftsResult.getValue() || allAircraftsResult.getValue().length === 0) {
        console.error('No aircrafts available to search')
        return Result.fail('No aircrafts available')
      }
      const filteredAircrafts = allAircraftsResult
        .getValue()
        .filter((aircraft) => ids.some((id) => id.equals(aircraft.id)))

      return Result.ok(filteredAircrafts)
    } catch (error) {
      console.error('Error in getAircraftBatch:', error)
      return Result.fail(`Error fetching aircraft batch: ${error}`)
    }
  }

  async saveAircraft(aircraft: Aircraft): Promise<Result<void>> {
    // A aeronave como este cliente a conhece, para o `_localToRemote` distinguir campo desconhecido
    // de campo apagado. Os dois chamadores montam uma entidade nova (`ConvertAircraftUnitsUseCase`
    // no caminho `toDomain`), então a do cache continua com os valores lidos; salvar a própria
    // instância do cache, já mutada, esconderia a limpeza.
    const savedAircraft = this.userAircrafts?.find((a) => a.id.equals(aircraft.id))
    const aircraftDTO = this._localToRemote(aircraft, savedAircraft)
    const response = await this.httpClient.post(`${this.getRemoteUrl()}/upsertAircraft`, aircraftDTO, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      }
    })

    if (response.success === false || response.status === 500) {
      return Result.fail('Internal server error')
    } else if (response.status < 200 || response.status >= 300) {
      return Result.fail('Failed to save user aircraft')
    }

    // updating local aircrafts
    this.userAircrafts = this.userAircrafts.filter((a) => !a.id.equals(aircraft.id))
    this.userAircrafts.push(aircraft)
    return Result.ok()
  }

  /**
   * Zera o ponteiro de perfil padrão da aeronave, e só ele.
   *
   * Existe porque a gravação comum **omite** `default_wb_profile_id` quando não há valor (PLA-315):
   * o campo não aparece no formulário, então uma aba velha carrega nulo sem o piloto ter visto nada,
   * e mandar esse nulo apagava o padrão escolhido em outro aparelho. O troco foi o ponteiro ficar
   * pendurado quando o perfil apontado é excluído — 135 das 626 aeronaves ativas com padrão
   * declarado já estão assim.
   *
   * Aqui a intenção é inequívoca, porque quem chama é a própria exclusão do perfil. Limpar a coluna
   * cabe ao servidor, na rota de exclusão (PLA-328); enquanto isso não existe, este é o caminho que
   * impede o cliente web de acrescentar ponteiros mortos à conta.
   */
  async clearDefaultWbProfile(aircraftId: UniqueEntityID): Promise<Result<void>> {
    const aircraft = this.userAircrafts?.find((a) => a.id.equals(aircraftId))
    if (!aircraft?.defaultWbProfileId) return Result.ok()

    return this._writeDefaultWbProfile(aircraft, null, 'Failed to clear the default weight and balance profile')
  }

  /**
   * Passa o ponteiro de perfil padrão de `fromProfileId` para `toProfileId`, e só ele — quando a
   * aeronave aponta mesmo para `fromProfileId`.
   *
   * Renomear um perfil cunha um id novo (a gravação do perfil é remoção mais criação), e o padrão
   * que apontava para o id aposentado morreria em silêncio. A gravação sai daqui, e não do
   * formulário da aeronave, porque o formulário pode ter outras mudanças que o piloto ainda não
   * decidiu salvar. A guarda pelo ponteiro conhecido é a mesma da limpeza: sem ela, uma aba velha
   * atropelaria o padrão escolhido em outro aparelho (PLA-315).
   */
  async moveDefaultWbProfile(
    aircraftId: UniqueEntityID,
    fromProfileId: UniqueEntityID,
    toProfileId: UniqueEntityID
  ): Promise<Result<void>> {
    const aircraft = this.userAircrafts?.find((a) => a.id.equals(aircraftId))
    if (!aircraft?.defaultWbProfileId?.equals(fromProfileId)) return Result.ok()

    return this._writeDefaultWbProfile(aircraft, toProfileId, 'Failed to move the default weight and balance profile')
  }

  private async _writeDefaultWbProfile(
    aircraft: Aircraft,
    defaultWbProfileId: UniqueEntityID | null,
    requestFailedMessage: string
  ): Promise<Result<void>> {
    const aircraftDTO: AircraftDTO = {
      ...this._localToRemote(aircraft),
      default_wb_profile_id: defaultWbProfileId ? defaultWbProfileId.toString() : null,
      updated_at: Date.now()
    }

    const response = await this.httpClient.post(`${this.getRemoteUrl()}/upsertAircraft`, aircraftDTO, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      }
    })

    if (response.success === false || response.status === 500) {
      return Result.fail('Internal server error')
    } else if (response.status < 200 || response.status >= 300) {
      return Result.fail(requestFailedMessage)
    }

    aircraft.defaultWbProfileId = defaultWbProfileId
    aircraft.updatedAt = new Date(aircraftDTO.updated_at)
    return Result.ok()
  }

  async deleteAircraft(id: UniqueEntityID): Promise<Result<void>> {
    const response = await this.httpClient.post(
      `${this.getRemoteUrl()}/removeAircraft`,
      {
        id: id.toString()
      },
      {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      }
    )

    if (response.success === false || response.status === 500) {
      return Result.fail('Internal server error')
    } else if (response.status < 200 || response.status >= 300) {
      return Result.fail('Failed to delete user aircraft')
    }

    // updating local aircrafts
    this.userAircrafts = this.userAircrafts.map((aircraft) => {
      if (!aircraft.id.equals(id)) {
        return aircraft
      }

      aircraft.deletedAt = new Date()
      return aircraft
    })
    return Result.ok()
  }

  // Private methods

  private async _getRemoteAircrafts(): Promise<Result<Aircraft[]>> {
    const response = await this.httpClient.get<RequestGetAircraftsResponse>(`${this.getRemoteUrl()}/getAircrafts`, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      }
    })

    if (response.success === false || response.status === 500) {
      return Result.fail('Internal server error')
    } else if (response.status < 200 || response.status >= 300) {
      return Result.fail('Failed to get user aircrafts')
    }
    return Result.ok(response.response.aircrafts?.map((aircraft) => this._remoteToLocal(aircraft)))
  }

  private _nullishCorrection(value: any) {
    return value === '' || value === 0 ? null : value
  }

  /**
   * Aeronaves criadas a partir de um modelo do catálogo com unidade fora do enum ("us gal/h") foram
   * salvas com essa unidade, e aí toda conversão de leitura devolvia null — a aeronave voltava sem
   * os campos correspondentes e não dava nem para editar. Os números gravados estão sempre em
   * unidade de domínio (o save converte antes de enviar), então cair na unidade de domínio quando o
   * valor não é reconhecido exibe o dado como está gravado, sem conversão.
   */
  private _sanitizeUnits(
    aircraft: AircraftDTO
  ): Pick<AircraftProps, 'fuelConsumptionUnit' | 'operationalCeilingUnit' | 'rateUnit' | 'speedUnit'> {
    return {
      fuelConsumptionUnit: normalizeUnit.fuelConsumption(aircraft.fuel_consumption_unit) ?? unitDomains.fuelConsumption,
      operationalCeilingUnit: normalizeUnit.altitude(aircraft.operational_ceiling_unit) ?? unitDomains.altitude,
      rateUnit: normalizeUnit.rate(aircraft.rate_unit) ?? unitDomains.rate,
      speedUnit: normalizeUnit.speed(aircraft.speed_unit) ?? unitDomains.speed
    }
  }

  private _remoteToLocal(aircraft: AircraftDTO): Aircraft {
    return Aircraft.create(
      {
        aircraftModelId: aircraft.aircraft_model_id ? new UniqueEntityID(aircraft.aircraft_model_id) : null,
        defaultWbProfileId: aircraft.default_wb_profile_id ? new UniqueEntityID(aircraft.default_wb_profile_id) : null,
        prefix: this._nullishCorrection(aircraft.prefix),
        model: this._nullishCorrection(aircraft.model),
        designatorIcao: this._nullishCorrection(aircraft.designator_icao),
        // Das aeronaves ativas sem tipo de combustível, a grande maioria carrega a coluna como
        // string vazia, e não nula (PLA-335). Era a única string do DTO fora do `_nullishCorrection`
        // dos vizinhos, então `''` atravessava tipado como `FuelType` e todo leitor que testasse com
        // `??` — em vez de `||` — o tomava por um tipo declarado.
        fuelType: this._nullishCorrection(aircraft.fuel_type),
        aircraftType: this._nullishCorrection(aircraft.aircraft_type),
        engineType: this._nullishCorrection(aircraft.engine_type),
        engineModel: this._nullishCorrection(aircraft.engine_model),
        ...this._sanitizeUnits(aircraft),
        speedAc: this._nullishCorrection(aircraft.speed_ac),
        speedCruise: this._nullishCorrection(aircraft.speed_cruise),
        speedDc: this._nullishCorrection(aircraft.speed_dc),
        rateAc: this._nullishCorrection(aircraft.rate_ac),
        rateDc: this._nullishCorrection(aircraft.rate_dc),
        operationalCeiling: this._nullishCorrection(aircraft.operational_ceiling),
        fuelConsumptionAc: this._nullishCorrection(aircraft.fuel_consumption_ac),
        fuelConsumptionCruise: this._nullishCorrection(aircraft.fuel_consumption_cruise),
        fuelConsumptionDc: this._nullishCorrection(aircraft.fuel_consumption_dc),
        fuelConsumptionTaxi: this._nullishCorrection(aircraft.fuel_consumption_taxi),
        deletedAt: this._nullishCorrection(aircraft.deleted_at) ? new Date(aircraft.deleted_at) : null,
        createdAt: new Date(aircraft.created_at),
        updatedAt: new Date(aircraft.updated_at)
      },
      new UniqueEntityID(aircraft.id)
    )
  }

  /**
   * O servidor grava a aeronave com o `save` do TypeORM: propriedade que chega `undefined` é
   * ignorada, e propriedade que chega **nula apaga a coluna**. Num campo numérico de aeronave zero
   * é "não informado" — `_nullishCorrection` converte zero em nulo na leitura dos dois lados —,
   * então mandar zero equivale a mandar nulo. Omitir a chave é a única forma de dizer "esta
   * gravação não sabe deste campo", e é o que o cliente móvel já faz.
   *
   * Sem isso, uma aba aberta antes de o outro cliente definir o perfil padrão apagava esse perfil
   * ao salvar qualquer outro campo da aeronave. Em produção, 626 aeronaves ativas têm perfil padrão
   * declarado e 135 já apontam para um perfil apagado.
   *
   * O troco é declarado, e alcança os dois campos: o nulo era o único caminho do piloto que limpava
   * a coluna. Excluir o perfil que era o padrão zera `defaultWbProfileId` no formulário e o ponteiro
   * passa a ficar pendurado — melhor que apagar por engano o padrão escolhido em outro aparelho;
   * quem deve limpar é a rota de exclusão do perfil no servidor, como o caminho de admin já faz. E
   * limpar o campo de consumo de táxi deixa de apagar o valor gravado: o anterior volta na próxima
   * leitura. (PLA-315)
   *
   * Por isso o `saved` — a aeronave como este cliente a conhece. Ele separa "não sei deste campo" de
   * "eu sabia e o piloto apagou": só o **segundo** manda a chave nula. É o que devolve o caminho de
   * limpeza sem reabrir o atropelo, porque o atropelo é exatamente o caso em que este cliente nunca
   * viu o valor — a aba velha carrega nulo, o cache diz nulo, e a chave continua omitida. (PLA-329)
   */
  /**
   * Os três estados de um campo que o servidor apaga com nulo, em uma expressão só: com valor, viaja
   * o valor; sem valor e sem valor conhecido, a chave não viaja; sem valor mas com valor conhecido, o
   * nulo viaja e apaga a coluna.
   */
  private _clearableField<K extends string, V>(key: K, value: V, savedValue: V): Record<K, V | null> | object {
    if (value) return { [key]: value } as Record<K, V>
    if (savedValue) return { [key]: null } as Record<K, null>
    return {}
  }

  private _localToRemote(aircraft: Aircraft, saved?: Aircraft): AircraftDTO {
    const defaultWbProfileId = aircraft.defaultWbProfileId?.toString()

    return {
      id: aircraft.id.toString(),
      aircraft_model_id: aircraft.aircraftModelId?.toString() || null,
      ...(defaultWbProfileId ? { default_wb_profile_id: defaultWbProfileId } : {}),
      prefix: aircraft.prefix,
      model: aircraft.model,
      designator_icao: aircraft.designatorIcao,
      fuel_type: aircraft.fuelType,
      aircraft_type: aircraft.aircraftType,
      engine_type: aircraft.engineType,
      engine_model: aircraft.engineModel,
      speed_unit: aircraft.speedUnit,
      rate_unit: aircraft.rateUnit,
      fuel_consumption_unit: aircraft.fuelConsumptionUnit,
      operational_ceiling_unit: aircraft.operationalCeilingUnit,
      speed_ac: aircraft.speedAc,
      speed_cruise: aircraft.speedCruise,
      speed_dc: aircraft.speedDc,
      rate_ac: aircraft.rateAc,
      rate_dc: aircraft.rateDc,
      fuel_consumption_ac: aircraft.fuelConsumptionAc,
      fuel_consumption_cruise: aircraft.fuelConsumptionCruise,
      fuel_consumption_dc: aircraft.fuelConsumptionDc,
      operational_ceiling: aircraft.operationalCeiling,
      ...this._clearableField('fuel_consumption_taxi', aircraft.fuelConsumptionTaxi, saved?.fuelConsumptionTaxi),
      created_at: aircraft.createdAt.getTime(),
      updated_at: aircraft.updatedAt.getTime(),
      deleted_at: aircraft.deletedAt?.getTime?.()
    }
  }
}
